import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Truck } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", registration_number: "", vehicle_type: "Bus", notes: "" };

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/vehicles");
    setVehicles(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const onSave = async () => {
    if (!form.name.trim() || !form.registration_number.trim()) {
      toast.error("Name and registration number are required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/vehicles/${editing}`, form);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", form);
        toast.success("Vehicle added");
      }
      setForm(empty);
      setEditing(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this vehicle and all its entries?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Deleted");
    load();
  };

  const onEdit = (v) => {
    setEditing(v.id);
    setForm({
      name: v.name,
      registration_number: v.registration_number,
      vehicle_type: v.vehicle_type || "Bus",
      notes: v.notes || "",
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Fleet</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Vehicles
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""} registered
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-vehicle-btn" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="vehicle-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? "Edit Vehicle" : "Add Vehicle"}
              </DialogTitle>
              <DialogDescription>
                Register a commercial vehicle with name, registration number, and type.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="v-name">Vehicle Name</Label>
                <Input
                  id="v-name"
                  data-testid="vehicle-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Bus 1, Tata Marcopolo"
                />
              </div>
              <div>
                <Label htmlFor="v-reg">Registration Number</Label>
                <Input
                  id="v-reg"
                  data-testid="vehicle-reg-input"
                  value={form.registration_number}
                  onChange={(e) =>
                    setForm({ ...form, registration_number: e.target.value })
                  }
                  placeholder="e.g., MH 12 AB 1234"
                />
              </div>
              <div>
                <Label htmlFor="v-type">Type</Label>
                <Input
                  id="v-type"
                  data-testid="vehicle-type-input"
                  value={form.vehicle_type}
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                  placeholder="Bus / Truck / Tempo"
                />
              </div>
              <div>
                <Label htmlFor="v-notes">Notes</Label>
                <Textarea
                  id="v-notes"
                  data-testid="vehicle-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onSave} data-testid="save-vehicle-btn">
                {editing ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {vehicles.length === 0 ? (
        <div className="card-swiss p-12 text-center" data-testid="empty-vehicles">
          <Truck className="h-10 w-10 mx-auto text-gray-300" />
          <p className="mt-3 text-gray-500">No vehicles yet — add your first vehicle.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => (
            <div key={v.id} className="card-swiss p-5" data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow">{v.vehicle_type}</div>
                  <div className="font-display text-xl font-bold tracking-tight mt-1">
                    {v.name}
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1">
                    {v.registration_number}
                  </div>
                </div>
                <Truck className="h-5 w-5 text-gray-400" />
              </div>
              {v.notes && (
                <p className="text-xs text-gray-500 mt-3 line-clamp-2">{v.notes}</p>
              )}
              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(v)}
                  data-testid={`edit-vehicle-${v.id}`}
                  className="gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDelete(v.id)}
                  data-testid={`delete-vehicle-${v.id}`}
                  className="gap-1.5 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
